import React, { type ReactNode, useMemo } from 'react';
import { App, ConfigProvider } from 'antd';
import { useIntl } from 'react-intl';
import type { Locale } from 'antd/lib/locale';
import antdLocaleUS from 'antd/locale/en_US';
import { getAntdTheme } from 'constants/antd.constants';
import { observer } from 'mobx-react';
import { useTheme } from 'hooks/use-theme';
import { css, Global } from '@emotion/react';
import { IconProvider } from '@ant-design/icons';
import { Empty, CompactEmpty } from 'components/common/empty';
import { TableEmptyState } from 'components/common/table/table-empty-state';

/**
 * This component is used to wrap the entire application.
 * The ConfigProvider sets the base tokens and the locales (translations) for the antd components.
 * The App component is used to set the base styles for the application (i.e. root font-size, root color, etc.)
 */

/**
 * A popup opened inside a Modal, Drawer or Popover is portalled into it. antd 5.29's zIndexContext seeds
 * a container from the global zIndexPopupBase (1000) + 100 unless the container is handed a zIndex, and
 * Popover is not: on document.body the TTL popover's Select, DatePicker and Tooltip would open at
 * 1150-1200, behind its 1400. The Modal and Drawer wrappers do hand theirs over (popup-layer.context.ts),
 * but a popup on document.body that is open while they animate in keeps its mid-animation position.
 */
const NESTED_POPUP_CONTAINERS = '.ant-modal-content, .ant-drawer-body, .ant-popover-inner';

export const getNestedPopupContainer = (triggerNode?: HTMLElement) =>
  triggerNode?.closest<HTMLElement>(NESTED_POPUP_CONTAINERS) ?? document.body;

// antd's defaultRenderEmpty keeps these compact: they render inside a popup.
const COMPACT_EMPTY_COMPONENTS = ['Select', 'TreeSelect', 'Cascader', 'Transfer', 'Mentions'];

// `Table`/`List` arrive here whenever a call site passes neither `emptyMessageId` nor `emptyComponent`
// — `table.tsx` leaves `locale` empty and antd falls through to `renderEmpty('Table')`. They get the
// same composition the propped case wires, so there is only ever one in-table empty state.
export const renderDesignSystemEmpty = (componentName?: string) => {
  if (componentName && COMPACT_EMPTY_COMPONENTS.includes(componentName)) {
    return <CompactEmpty />;
  }

  return componentName === 'Table' || componentName === 'List' ? <TableEmptyState type="no-data" /> : <Empty />;
};

const AntdWrapper: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const intl = useIntl();
  const { appTheme } = useTheme();

  const theme = useMemo(() => getAntdTheme(appTheme), [appTheme]);

  const locale = useMemo<Locale>(
    () => ({
      ...antdLocaleUS,
      Table: {
        ...antdLocaleUS.Table,
        triggerAsc: intl.formatMessage({ id: 'sort.ascending' }),
        triggerDesc: intl.formatMessage({ id: 'sort.descending' }),
        cancelSort: intl.formatMessage({ id: 'sort.cancel' })
      }
    }),
    [intl]
  );
  return (
    <ConfigProvider
      csp={{ nonce: window.nonce }}
      theme={theme}
      locale={locale}
      getPopupContainer={getNestedPopupContainer}
      renderEmpty={renderDesignSystemEmpty}>
      <IconProvider value={{ csp: { nonce: window.nonce } }}>
        <Global styles={globalStyles} />
        <App>{children}</App>
      </IconProvider>
    </ConfigProvider>
  );
};

export default observer(AntdWrapper);

// The scrollbar is themed in `@env0/ui-theme/globals.css`, not here.
export const globalStyles = css`
  // antd 5.29 pads the box from Collapse's \`contentPadding\` and \`borderlessContentPadding\`, but a ghost
  // Collapse pads it with \`paddingSM\`, which also sets the header's padding. !important because antd's
  // own selector is the more specific.
  .ant-collapse-content-box {
    padding: 0 !important;
  }

  // antd 5.29 zeroes the line height of the svg inside a button's icon slot but not of the slot itself,
  // so .anticon's vertical-align: -0.125em adds 1.75px of descent under a 16px glyph and the icon rides
  // ~1px above the label. Global because IconButton styles antd's raw Button and never routes through
  // common/button.tsx.
  .ant-btn-icon {
    line-height: 0;
  }

  // antd hard-codes a normal (400) weight on these parts, and 400 is now Regular. Figma draws them as
  // Body: select values and options, dropdown menu items, input addons, popover content and date-picker
  // cells. antd has no component token for them. Each selector repeats its last class, so it outranks
  // antd's :where()-scoped rule by one class. Popover titles keep the heading role: antd sets their
  // weight on .ant-popover-title itself.
  .ant-select .ant-select-selection-item.ant-select-selection-item,
  .ant-select-dropdown .ant-select-item.ant-select-item,
  .ant-dropdown .ant-dropdown-menu-item.ant-dropdown-menu-item,
  .ant-dropdown .ant-dropdown-menu-submenu-title.ant-dropdown-menu-submenu-title,
  .ant-input-group-wrapper .ant-input-group-addon.ant-input-group-addon,
  .ant-popover.ant-popover,
  .ant-picker-dropdown .ant-picker-content.ant-picker-content th,
  .ant-picker-dropdown .ant-picker-content.ant-picker-content td {
    font-weight: var(--type-weight-body);
  }
`;
